import { getBuildInfo } from "@um/ui";
import { Capacitor } from "@capacitor/core";
import { gte, valid } from "semver-ts";
import { useAutoUpdateStore } from "@/store/useAutoUpdateStore";
import { initializeAutoUpdate, applyPendingUpdate } from "./autoUpdateService";

/** The existing release command publishes the native bundle before web hosting.
 * Record the paired build's version; don't make onboarding re-check deployment.
 */
export async function webHandoffVersion(): Promise<string | undefined> {
  if (import.meta.env.DEV || import.meta.env.VITE_USE_EMULATORS === "true")
    return;
  const build = await getBuildInfo();
  return build?.version && valid(build.version) ? build.version : undefined;
}

/** Called only after verifying a web handoff and its account's saved setup. */
export async function ensureWebHandoffVersion(
  minimum: string | undefined,
  isCurrent: () => boolean
): Promise<boolean> {
  // Imported production checkpoints carry a native bundle requirement. A
  // browser running the local app experience has no native bundle to update.
  if (
    !Capacitor.isNativePlatform() &&
    (import.meta.env.DEV || import.meta.env.VITE_USE_EMULATORS === "true")
  ) return true;
  if (!minimum) return true; // Legacy records; this running code supports them.
  if (!valid(minimum)) return false;
  const current = useAutoUpdateStore.getState().currentBundle?.version;
  if (current && valid(current) && gte(current, minimum)) return true;
  const result = await initializeAutoUpdate(minimum);
  if (!isCurrent()) return false;
  if (
    result.isIncompatible ||
    !result.bundle?.version ||
    !valid(result.bundle.version) ||
    !gte(result.bundle.version, minimum)
  )
    return false;
  await useAutoUpdateStore.getState().downloadUpdate(result.bundle);
  if (isCurrent()) await applyPendingUpdate();
  return false; // Applying reloads the app; never start the flow on the old bundle.
}
